import { useTheme } from '@emotion/react'
import { WebLink } from '@news-mono/web-common'
import {
    SendgridEnv,
    SendgridSuppressionGroup,
} from '@west-australian-newspapers/email-queue-message-types'
import React, { useState } from 'react'
import { Button } from '../buttons/Button/Button'
import { useRecaptcha } from '../recaptcha/Recaptcha'
import { themedValue } from '../__styling'
import {
    NewsletterSubscriptionOptions,
    NewsletterSubscriptionOptionsValues,
} from './components/NewsletterSubscriptionOptions/NewsletterSubscriptionOptions'
import { getNewsletterSubscriptionOptions } from './config'
import {
    StyledBody,
    StyledButton,
    StyledError,
    StyledHeading,
    StyledInput,
    StyledLabel,
    StyledNewsletterSubscription,
    StyledTerms,
} from './NewsletterSubscription.styled'

interface NewsletterSubscriptionFormState {
    optOutLists: SendgridSuppressionGroup[]
    firstName?: string
    lastName?: string
    email?: string
}

interface NewsletterSubscriptionProps {
    sendgridEnv: SendgridEnv
}

export const NewsletterSubscription: React.FC<NewsletterSubscriptionProps> = ({
    sendgridEnv,
}) => {
    const theme = useTheme()
    const { executeRecaptcha } = useRecaptcha()
    const [formState, setformState] =
        useState<'ready' | 'submitting' | 'success' | 'error'>('ready')
    const [formData, setformData] = useState<NewsletterSubscriptionFormState>({
        firstName: undefined,
        lastName: undefined,
        email: undefined,
        optOutLists: [],
    })
    const [error, setError] = useState<string | null>(null)

    const options = getNewsletterSubscriptionOptions(theme, sendgridEnv)

    const heading = themedValue(theme, {
        thewest: 'The West Newsletters',
        perthnow: 'PerthNow Newsletters',
        sevennews: '7NEWS Newsletters',
    })

    const handleFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        e.preventDefault()
        setformData({
            ...formData,
            [e.target.name]: e.target.value.trim(),
        })
    }

    const handleSubscriptionOptionChange = (
        options: NewsletterSubscriptionOptionsValues,
    ) => {
        const optOutLists = Object.keys(options).filter(
            (key) => options[key as SendgridSuppressionGroup],
        ) as SendgridSuppressionGroup[]
        setformData({ ...formData, optOutLists })
    }

    const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault()
        setformState('submitting')
        setError(null)
        const token = await executeRecaptcha({ action: 'subscribe' })

        const result = await fetch('/newsletter-subscribe', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
            },
            body: JSON.stringify({
                token,
                ...formData,
            }),
        })

        if (result.status === 201) {
            setformState('success')
            return
        }

        setformState('error')
        setError(await result.text())
    }

    if (formState === 'success') {
        return <NewsletterSubscriptionSuccess />
    }

    return (
        <StyledNewsletterSubscription onSubmit={handleFormSubmit}>
            <StyledHeading>{heading}</StyledHeading>
            <StyledBody>
                Select the emails that interest you and enter your details below
                to subscribe to our news. You can opt-out by selecting the
                unsubscribe link at the bottom of any newsletter we send you.
            </StyledBody>
            <NewsletterSubscriptionOptions
                options={options}
                disabled={formState === 'submitting'}
                onChange={handleSubscriptionOptionChange}
            />
            <StyledLabel htmlFor="firstName">First name</StyledLabel>
            <StyledInput
                type="text"
                name="firstName"
                disabled={formState === 'submitting'}
                onChange={handleFormChange}
            />
            <StyledLabel htmlFor="lastName">Last name</StyledLabel>
            <StyledInput
                type="text"
                name="lastName"
                disabled={formState === 'submitting'}
                onChange={handleFormChange}
            />
            <StyledLabel htmlFor="email">Email address*</StyledLabel>
            <StyledInput
                type="email"
                name="email"
                required
                disabled={formState === 'submitting'}
                onChange={handleFormChange}
            />
            <StyledTerms>
                By providing us with your details, you agree to our{' '}
                <WebLink target="_blank" to="/terms">
                    Terms of Use
                </WebLink>{' '}
                and{' '}
                <WebLink
                    target="_blank"
                    to="https://www.sevenwestmedia.com.au/privacy-policies/privacy"
                >
                    Privacy Statement.
                </WebLink>
            </StyledTerms>
            {formState === 'error' && (
                <StyledError>
                    Something went wrong <br />
                    {error} <br />
                    Please try again later.
                </StyledError>
            )}
            <StyledButton
                disabled={formState === 'submitting'}
                buttonWidth="wide"
                type="submit"
            >
                Subscribe
            </StyledButton>
        </StyledNewsletterSubscription>
    )
}

export const NewsletterSubscriptionSuccess = () => (
    <StyledNewsletterSubscription>
        <StyledHeading>Thanks for subscribing.</StyledHeading>
        <StyledBody>
            If you don’t wish to receive these in the future, you may opt-out by
            selecting the unsubscribe link at the bottom of any newsletter we
            send you.
        </StyledBody>

        <WebLink to="/">
            <Button buttonWidth="wide">Return to home</Button>
        </WebLink>
    </StyledNewsletterSubscription>
)
